import {
    EVENT_ADOPTION_METRICS,
    buildEventAdoptionAllUsersIncrease,
    buildEventAdoptionAllUsersDecrease,
    parseEventAdoptionResults,
} from '@pendo/middleware';
import { i18n_t as $t } from '@/utils/i18n';
import moment from '@/utils/moment-wrapper';
import { createTimeSeriesFromDate } from '@/common/time-series-picker/time-series-picker.util';
import { getPreviousTimeSeries } from '@/utils/time.utils';

export const ADOPTION_METRICS = EVENT_ADOPTION_METRICS;

const COUNTABLE_KINDS = ['feature', 'page', 'trackEvent'];

const METRIC_BUILDERS = {
    [ADOPTION_METRICS.ALL_USERS_INCREASE]: buildEventAdoptionAllUsersIncrease,
    [ADOPTION_METRICS.ALL_USERS_DECREASE]: buildEventAdoptionAllUsersDecrease,
};

export function getInitialState () {
    return {
        insights: null,
        insightRequestBody: null,
        isLoading: false,
        error: null,
    };
}

export function determineAppId (rootGetters) {
    // Remove this once we have config blade pulling the app id
    const defaultApp = rootGetters['apps/allWebPlusIntegratedMobileApps']?.[0];
    if (defaultApp?.id) {
        return defaultApp.id;
    }

    return -323232;
}

export function computeAdoptionTimeRangeStartAndEnd (offsetDays = 7) {
    const end = moment(undefined, { ignoreTimeZone: true }).subtract(1, 'days');
    const start = end.clone().subtract(offsetDays - 1, 'days');

    return {
        start: start.format('MMMM D'),
        end: end.format('MMMM D'),
    };
}

export function computeAdoptionTimeSeries (offsetDays = 7) {
    const end = moment(undefined, { ignoreTimeZone: true }).subtract(1, 'days');
    const start = end.clone().subtract(offsetDays - 1, 'days');

    return createTimeSeriesFromDate(
        { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') },
        true,
    );
}

export function computePreviousAdoptionTimeSeries (currentTimeSeries) {
    return getPreviousTimeSeries(currentTimeSeries);
}

export function buildRequestBody (appId, countableIds = [], includeAnonymous = false, { currentTimeSeries, previousTimeSeries, timeRangeStart, timeRangeEnd } = {}) {
    const jobSpec = {
        app_id: appId,
        metrics: [
            { metric_type: 'adoption_all_users_increase' },
            { metric_type: 'adoption_all_users_decrease' },
        ],
        offset_days: 7,
        trend_type: 'adoption',
        countable_ids: countableIds,
        include_anonymous: includeAnonymous,
    };

    if (currentTimeSeries?.spec) {
        jobSpec.current_time_series = {
            first: currentTimeSeries.spec.first,
            period: currentTimeSeries.spec.period,
            count: currentTimeSeries.spec.count,
        };
    }

    if (previousTimeSeries?.spec) {
        jobSpec.previous_time_series = {
            first: previousTimeSeries.spec.first,
            period: previousTimeSeries.spec.period,
            count: previousTimeSeries.spec.count,
        };
    }

    return { job_spec: jobSpec, timeRangeStart, timeRangeEnd };
}

function buildAppIcon (rawIcon) {
    if (typeof rawIcon === 'string') {
        return { type: rawIcon, size: 12 };
    }
    if (rawIcon) {
        return { ...rawIcon, size: 12 };
    }

    return null;
}

function getCountableIcon (countableKind) {
    const kindMap = {
        Feature: 'mouse-pointer',
        Page: 'file',
        TrackType: 'zap',
    };

    return kindMap[countableKind] || 'feature';
}

function getTitle (countableKind) {
    const kindTitleMap = {
        Feature: $t('trends.Feature adoption'),
        Page: $t('trends.Page adoption'),
        TrackType: $t('trends.Track event adoption'),
    };

    return kindTitleMap[countableKind] || $t('trends.Adoption');
}

export function buildAppData (rootGetters, appId) {
    const appDisplayName = rootGetters['trendsSettings/getAppDisplayName'];
    const trendsApp = rootGetters['trendsSettings/getApp'];
    const appIcon = rootGetters['apps/getIconByAppId']?.(appId);

    return {
        name: appDisplayName || trendsApp?.name,
        icon: appIcon,
        appId,
    };
}

export function splitCountableIdsByKind (countableIds = []) {
    const featureIds = [];
    const pageIds = [];
    const trackEventIds = [];
    countableIds.forEach((countable) => {
        if (!countable?.id || !countable?.kind) {
            return;
        }
        if (countable.kind === 'Feature') {
            featureIds.push(countable.id);
        } else if (countable.kind === 'Page') {
            pageIds.push(countable.id);
        } else if (countable.kind === 'TrackType') {
            trackEventIds.push(countable.id);
        }
    });

    return { featureIds, pageIds, trackEventIds };
}

export function buildAdoptionAggregations ({ appId, groupId, hasShowAnonymousFlag, featureIds = [], pageIds = [], trackEventIds = [] }) {
    const isGroup = !!groupId;
    const kinds = isGroup ? [undefined] : COUNTABLE_KINDS;
    const aggregations = [];

    Object.entries(METRIC_BUILDERS).forEach(([metric, builder]) => {
        kinds.forEach((kind) => {
            const options = {
                appId,
                hasShowAnonymousFlag,
                featureIds: featureIds.length ? featureIds : undefined,
                pageIds: pageIds.length ? pageIds : undefined,
                trackEventIds: trackEventIds.length ? trackEventIds : undefined,
                groupIds: isGroup ? [groupId] : undefined,
            };
            const { name, pipeline, productArea, subProductArea } = builder(options, kind);

            aggregations.push({
                metric,
                kind,
                name: `${name}-${kind ?? 'group'}`,
                productArea,
                subProductArea,
                responseFormat: 'rows',
                build: () => pipeline,
            });
        });
    });

    return aggregations;
}

function buildAdoptionText (parsedResult, appData, hasSelectedEvents) {
    const { metric, adoptionRateCurrent, adoptionRatePrevious, delta } = parsedResult;
    const currentRate = adoptionRateCurrent ?? 0;
    const previousRate = adoptionRatePrevious ?? 0;
    const absChange = delta != null ? Math.abs(delta) : 0;

    if (metric === ADOPTION_METRICS.ALL_USERS_INCREASE) {
        return {
            textBeforeTagName: $t('signals.eventAdoptionTextBefore'),
            textAfterTagName: hasSelectedEvents
                ? $t('signals.eventAdoptionIncreaseTextAfterSelectedEvents', { absChange })
                : $t('signals.eventAdoptionIncreaseTextAfterApp', { absChange }),
        };
    }

    return {
        textBeforeTagName: $t('signals.eventAdoptionTextBefore'),
        textAfterTagName: hasSelectedEvents
            ? $t('signals.eventAdoptionDecreaseTextAfterSelectedEvents', { absChange, previousRate, currentRate })
            : $t('signals.eventAdoptionDecreaseTextAfterApp', { absChange, previousRate, currentRate }),
    };
}

function buildTrendFromParsedResult (parsedResult, appData, hasSelectedEvents) {
    const { metric, countableKind, countableId, countableName, adoptionRateCurrent, adoptionRatePrevious, delta, trendDirection } = parsedResult;

    const { textBeforeTagName, textAfterTagName } = buildAdoptionText(parsedResult, appData, hasSelectedEvents);

    const appIcon = buildAppIcon(appData?.icon);
    const tagIcon = { type: getCountableIcon(countableKind), size: 12 };

    return {
        title: getTitle(countableKind),
        textBeforeTagName,
        textAfterTagName,
        showAppTag: true,
        tagName: countableName,
        tagIcon,
        appName: appData?.name,
        appIcon,
        delta,
        trendDirection,
        adoptionRateCurrent,
        adoptionRatePrevious,
        countableId,
        countableKind,
        metric,
        type: 'adoption',
        appId: appData?.appId,
    };
}

export function transformAggregationResults (aggregations, rawResults, appData, hasSelectedEvents) {
    const parsedResults = parseEventAdoptionResults(aggregations, rawResults);

    return parsedResults.map((parsedResult) => buildTrendFromParsedResult(parsedResult, appData, hasSelectedEvents));
}
